// What the Pick menu offers, worked out in one place because two surfaces need
// different answers and both have to be right.
//
// A section is `{ name, ids }` — a heading and the files under it. Picking one
// puts its files in the selection; picking it again takes them back out, so a
// selection can be BUILT from several.
//
// THE RULE: a surface that has its own headings supplies them, and the picker
// mirrors what is on screen. Only a surface with no headings of its own falls
// back to grouping by file type.
//
// Why it matters that the Inspection tab supplies its own: every inspection
// file carries file_type "Inspector", so the fallback would collapse six visits
// into one heading called "Inspector" — a menu that cannot express the thing it
// was asked for ("let them choose inspection 1, inspection 2, or zip all",
// Karen 2026-08-07). It is the same collision that put 152 files under one
// heading on the Files tab on 2026-08-06.

/**
 * @param {Array<{id: number|string, file_type?: string}>} files the files this
 *   instance is showing — the authority on what may appear in a section.
 * @param {Array<{name: string, ids: Array<number|string>}>|null} groups the
 *   surface's own headings, or null to group by file type.
 */
export function pickSections(files = [], groups = null) {
  if (!groups) {
    const sections = [];
    for (const file of files) {
      const name = file.file_type || "Other";
      const found = sections.find((section) => section.name === name);
      if (found) found.ids.push(file.id);
      else sections.push({ name, ids: [file.id] });
    }
    return sections;
  }

  // Supplied groups are filtered through what is actually on screen. Two things
  // this catches: a file deleted since the tab last loaded (a stale id would put
  // a tile in the selection that no longer exists), and a heading whose files
  // are all on some OTHER instance — the Files tab switch turns whole sections
  // on and off beneath a picker that is handed the full list either way.
  const shown = new Set(files.map((file) => String(file.id)));
  return groups
    .map((group) => ({
      name: group.name,
      ids: (group.ids || []).filter((id) => shown.has(String(id))),
    }))
    // An inspection with no files yet is a real row on the Inspection tab, and a
    // menu item that picks nothing is worse than no menu item.
    .filter((group) => group.ids.length > 0);
}
